import {Button, Popover, Text, Stack} from '@mantine/core';
import React, {useEffect, useRef, useState} from 'react';

import {signal} from "@preact/signals-react";
import {useSignals} from '@preact/signals-react/runtime';
import {useIsMobile} from '@/utils/user_agent';
import {useSelectorWithRefresh} from '@/store/kysely_module';
import {useCurrentWorkspaceId} from '@/store/modules/workspaces_module';

type CommentFormProps = {
    onSubmit: (text: string, mentions?: string[]) => Promise<void>;
    initialValue?: string;
    onCancel?: () => void;
    isReply?: boolean;
    onInitialFocus?: () => string;
    newTimestamp?: {timestamp: string} | null;
    scrollIntoView?: {scrollIntoView: boolean};
    autoFocus?: boolean;
};

type MentionSuggestion = {
    id: string;
    display: string;
};

export const signalCommentTextareaIsFocused = signal(false);

const getCaretCoordinates = (element: HTMLTextAreaElement) => {
    const {selectionStart} = element;
    const div = document.createElement('div');
    const style = getComputedStyle(element);

    ['fontFamily', 'fontSize', 'fontWeight', 'letterSpacing', 'lineHeight',
     'padding', 'border', 'whiteSpace', 'wordWrap', 'width'].forEach(prop => {
        div.style[prop as any] = style[prop as any];
    });

    div.style.position = 'absolute';
    div.style.visibility = 'hidden';
    div.style.whiteSpace = 'pre-wrap';
    div.style.wordWrap = 'break-word';

    const textBeforeCaret = element.value.substring(0, selectionStart);
    div.textContent = textBeforeCaret;

    const span = document.createElement('span');
    span.textContent = element.value.substring(selectionStart) || '.';
    div.appendChild(span);

    document.body.appendChild(div);

    const rect = element.getBoundingClientRect();
    const spanRect = span.getBoundingClientRect();

    const coordinates = {
        top: spanRect.top - rect.top + element.scrollTop,
        left: spanRect.left - rect.left + element.scrollLeft,
    };

    document.body.removeChild(div);

    return coordinates;
};

export const CommentFormCustom = (props: CommentFormProps) => {
    useSignals();

    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const workspaceId = useCurrentWorkspaceId();

    const [draft, setDraft] = useState(props.initialValue || '');
    const [showMentions, setShowMentions] = useState(false);
    const [mentionSearch, setMentionSearch] = useState('');
    const [mentionPosition, setMentionPosition] = useState({top: 0, left: 0});
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [mentionStartPos, setMentionStartPos] = useState(0);

    const submitButtonText = props.isReply ? 'Add Reply' : 'Submit';

    const isMobile = useIsMobile();

    const [workspaceUsers] = useSelectorWithRefresh('getUsersForWorkspace', {
        workspace_id: workspaceId,
        excludeSelf: false
    }, [], {workspace_id: workspaceId});

    const mentionUsers: MentionSuggestion[] = workspaceUsers.map((user: any) => ({
        id: user.id,
        display: user.name || user.email || 'Unknown User'
    }));

    const filteredMentions = mentionUsers.filter(user =>
        user.display.toLowerCase().includes(mentionSearch.toLowerCase())
    );

    useEffect(() => {
        if (props.scrollIntoView && !isMobile) {
            setTimeout(() => {
                window.scrollBy({ top: (textareaRef?.current?.getBoundingClientRect().top || 0) - 400, behavior: 'smooth' });
                textareaRef.current?.focus();
                if (textareaRef.current) {
                    const length = textareaRef.current.value.length;
                    textareaRef.current.setSelectionRange(length, length);
                }
            }, 100);
        }
    }, [props.scrollIntoView, isMobile]);

    useEffect(() => {
        if ((props.initialValue || props.isReply) && textareaRef.current) {
            setTimeout(() => {
                textareaRef.current?.focus();
                if (textareaRef.current) {
                    const length = textareaRef.current.value.length;
                    textareaRef.current.setSelectionRange(length, length);
                }
            }, 0);
        }
    }, [props.initialValue, props.isReply]);

    useEffect(() => {
        if (props.autoFocus && textareaRef.current) {
            setTimeout(() => {
                textareaRef.current?.focus();
            }, 0);
        }
    }, [props.autoFocus]);

    useEffect(() => {
        if (props.newTimestamp) {
            setDraft(`${draft ? `${draft}\n` : ''}${props.newTimestamp.timestamp} `);
            setTimeout(() => {
                textareaRef.current?.focus();
                if (textareaRef.current) {
                    const length = textareaRef.current.value.length;
                    textareaRef.current.setSelectionRange(length, length);
                }
            }, 0);
        }
    }, [props.newTimestamp]);

    const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        const cursorPos = e.target.selectionStart;

        setDraft(value);

        const textBeforeCursor = value.substring(0, cursorPos);
        const atMatch = textBeforeCursor.match(/@([a-zA-Z0-9._]*)$/);

        console.log('textBeforeCursor:', textBeforeCursor);
        console.log('atMatch:', atMatch);
        console.log('filteredMentions:', filteredMentions);

        if (atMatch) {
            const search = atMatch[1];
            setMentionSearch(search);
            setMentionStartPos(cursorPos - search.length - 1);
            setSelectedIndex(0);

            if (textareaRef.current) {
                const coords = getCaretCoordinates(textareaRef.current);
                console.log('coords:', coords);
                setMentionPosition(coords);
            }

            setShowMentions(true);
            console.log('showMentions set to true');
        } else {
            setShowMentions(false);
        }
    };

    const insertMention = (user: MentionSuggestion) => {
        const before = draft.substring(0, mentionStartPos);
        const after = draft.substring(textareaRef.current?.selectionStart || 0);
        const newValue = `${before}@[${user.display}](${user.id}) ${after}`;

        setDraft(newValue);
        setShowMentions(false);

        setTimeout(() => {
            if (textareaRef.current) {
                const newCursorPos = before.length + `@[${user.display}](${user.id}) `.length;
                textareaRef.current.focus();
                textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
            }
        }, 0);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (!showMentions) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex(prev => (prev + 1) % filteredMentions.length);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex(prev => (prev - 1 + filteredMentions.length) % filteredMentions.length);
        } else if (e.key === 'Enter' && filteredMentions.length > 0) {
            e.preventDefault();
            insertMention(filteredMentions[selectedIndex]);
        } else if (e.key === 'Escape') {
            setShowMentions(false);
        }
    };

    const extractMentions = (text: string) => {
        const mentionRegex = /@\[([^\]]+)\]\(([^)]+)\)/g;
        const mentions: string[] = [];
        let match;

        while ((match = mentionRegex.exec(text)) !== null) {
            mentions.push(match[2]);
        }

        return mentions;
    };

    const stripMentionMarkup = (text: string) => {
        return text.replace(/@\[([^\]]+)\]\(([^)]+)\)/g, '@$1');
    };

    const handleTextareaClick = (event: React.MouseEvent) => {
        event.preventDefault();

        document.querySelector('main')!.scrollTo({
            top: 0,
            behavior: 'smooth',
        });
    };

    return (
        <form
            id='comment-form'
            onSubmit={async (e) => {
                e.preventDefault();
                const mentions = extractMentions(draft);
                const cleanText = stripMentionMarkup(draft);
                await props.onSubmit(cleanText, mentions);
                setDraft('');
            }}
        >
            <div style={{position: 'relative'}}>
                <textarea
                            ref={textareaRef}
                            value={draft}
                            onChange={handleTextChange}
                            onKeyDown={handleKeyDown}
                            onClick={handleTextareaClick}
                            onFocus={() => {
                                signalCommentTextareaIsFocused.value = true;
                            }}
                            onBlur={() => {
                                setTimeout(() => {
                                    signalCommentTextareaIsFocused.value = false;
                                    setShowMentions(false);
                                }, 200);
                            }}
                            placeholder="Write a comment..."
                            style={{
                                maxWidth: '600px',
                                minHeight: '120px',
                                width: '100%',
                                border: '1px solid #ced4da',
                                borderRadius: '4px',
                                padding: '8px 12px',
                                fontSize: '14px',
                                lineHeight: 1.4,
                                backgroundColor: 'var(--mantine-color-dark-6)',
                                color: 'var(--mantine-color-text)',
                                outline: 0,
                                resize: 'vertical',
                            }}
                        />

                {showMentions && filteredMentions.length > 0 && (
                    <div
                        style={{
                            position: 'absolute',
                            top: `${mentionPosition.top + 24}px`,
                            left: `${mentionPosition.left}px`,
                            zIndex: 1000,
                            backgroundColor: 'var(--mantine-color-dark-6)',
                            border: '1px solid var(--mantine-color-dark-4)',
                            borderRadius: '4px',
                            minWidth: '200px',
                            maxHeight: '200px',
                            overflowY: 'auto',
                        }}
                    >
                        <Stack gap={0}>
                            {filteredMentions.map((user, index) => (
                                <div
                                    key={user.id}
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        insertMention(user);
                                    }}
                                    onMouseEnter={() => setSelectedIndex(index)}
                                    style={{
                                        padding: '8px 12px',
                                        cursor: 'pointer',
                                        backgroundColor: index === selectedIndex
                                            ? 'var(--mantine-color-dark-5)'
                                            : 'transparent',
                                        fontSize: '14px',
                                    }}
                                >
                                    <Text size="sm">{user.display}</Text>
                                </div>
                            ))}
                        </Stack>
                    </div>
                )}
            </div>

            <div style={{height: '10px'}} />

            <div>
                <Button
                    className='comment-submit-button'
                    type='submit'
                    disabled={!draft.length}
                >
                    {submitButtonText}
                </Button>
                {props.onCancel && (
                    <Button
                        variant='subtle'
                        type='button'
                        onClick={props.onCancel}
                        style={{marginLeft: '5px'}}
                    >
                        Cancel
                    </Button>
                )}
            </div>

            <div style={{height: '20px'}} />
        </form>
    );
};
